"use client";

/**
 * AIDisclosureEditor — the "🍞 How was this made?" control: sets a track's
 * Human ↔ AI provenance (the Music Ingredients Label). Mirrors the bulk-upload
 * section so single uploads + the Media Vault edit form can set it too — without
 * it, tracks never carry an aiDisclosure, so the label never shows on the
 * frontend. Reusable + controlled.
 */

import { AI_LEVELS, AI_PERCENT_PRESETS, type AIDisclosure } from "@/data/ai-disclosure";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import Link from "next/link";
import { GraduationCap } from "lucide-react";

export function AIDisclosureEditor({
  value,
  onChange,
}: {
  value?: AIDisclosure | null;
  onChange: (d: AIDisclosure) => void;
}) {
  const d: AIDisclosure = value && value.level ? value : { level: "human" };
  const set = (patch: Partial<AIDisclosure>) => onChange({ ...d, ...patch });

  return (
    <div className="space-y-3">
      <div>
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <Label className="text-sm font-semibold flex items-center gap-1.5">🍞 How was this made?</Label>
          {/* Who owns what, crediting, and reading an AI tool's contract —
              covered in BB Academy so this isn't just a form field with no
              explanation (Gordon 2026-09-28). */}
          <Link
            href="/academy/ai-copyright-ownership"
            className="text-xs text-primary hover:underline inline-flex items-center gap-1 flex-shrink-0"
          >
            <GraduationCap className="h-3 w-3" />
            Who owns what? BB Academy
          </Link>
        </div>
        <p className="text-xs text-muted-foreground">The honest &ldquo;ingredients label&rdquo; — shown on the track page (Human ↔ AI).</p>
      </div>

      <div className="flex flex-wrap gap-2">
        {AI_LEVELS.map((lvl) => (
          <button
            key={lvl.value}
            type="button"
            onClick={() => set({ level: lvl.value })}
            className={cn(
              "flex-1 min-w-[140px] rounded-lg border-2 p-2 text-left transition-colors",
              d.level === lvl.value ? "border-primary bg-primary/10" : "border-border hover:border-primary/40",
            )}
          >
            <span className="text-sm font-semibold">{lvl.emoji} {lvl.label}</span>
            <span className="block text-[11px] text-muted-foreground leading-tight mt-0.5">{lvl.blurb}</span>
          </button>
        ))}
      </div>

      {d.level !== "human" && (
        <div className="space-y-3 rounded-lg border border-violet-500/30 bg-violet-500/5 p-3">
          {/* Quick AI % Preset Selector */}
          <div>
            <Label className="text-xs mb-1.5 block font-semibold text-violet-300">Quick Percentage Presets</Label>
            <div className="flex flex-wrap gap-1.5">
              {AI_PERCENT_PRESETS.filter(p => p.percent > 0).map((preset) => {
                const isSelected = d.aiPercent === preset.percent;
                return (
                  <button
                    key={preset.percent}
                    type="button"
                    onClick={() => {
                      const pub = preset.compositionHuman ? 0 : (preset.percent === 75 ? 100 : preset.percent);
                      const rec = preset.compositionHuman ? 100 : preset.percent;
                      set({
                        aiPercent: preset.percent,
                        publishingAiPercent: pub,
                        recordingAiPercent: rec,
                        level: preset.level,
                        compositionHuman: preset.compositionHuman ?? d.compositionHuman,
                      });
                    }}
                    className={cn(
                      "px-2.5 py-1 rounded-md text-xs font-medium border transition-all flex items-center gap-1",
                      isSelected
                        ? "bg-violet-600 text-white border-violet-400 shadow-sm"
                        : "bg-background/80 hover:bg-violet-500/20 border-border text-foreground"
                    )}
                    title={preset.description}
                  >
                    <span>{preset.emoji}</span>
                    <span>{preset.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* 2-Side Rights Breakdown: Publishing vs Recording */}
          {(() => {
            const pubPct = d.publishingAiPercent ?? (d.compositionHuman ? 0 : (d.aiPercent ?? 100));
            const recPct = d.recordingAiPercent ?? (d.compositionHuman ? 100 : (d.aiPercent ?? 100));
            const overallPct = Math.round((pubPct * 0.5) + (recPct * 0.5));

            return (
              <div className="rounded-lg border border-violet-500/40 bg-violet-500/10 p-3 space-y-3">
                <div className="flex items-center justify-between flex-wrap gap-1">
                  <h4 className="text-xs font-semibold uppercase tracking-wider text-violet-300 flex items-center gap-1.5">
                    ⚖️ 2-Sided Provenance Breakdown (Publishing ↔ Master Recording)
                  </h4>
                  <span className="text-xs font-mono font-bold px-2 py-0.5 rounded bg-violet-600/40 text-violet-200 border border-violet-400/30">
                    Overall: {overallPct}% AI
                  </span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {/* 1. Publishing Side: Composition & Lyrics */}
                  <div className="space-y-1.5 p-2.5 rounded-md border bg-background/80">
                    <div className="flex items-center justify-between">
                      <Label className="text-xs font-semibold text-foreground flex items-center gap-1">
                        🎼 1. Publishing Side
                      </Label>
                      <span className="text-xs font-mono font-bold text-sky-400">
                        {pubPct}% AI
                      </span>
                    </div>
                    <p className="text-[11px] text-muted-foreground leading-tight">
                      Music composition &amp; lyrics (melody, chords, song words).
                    </p>
                    <div className="flex items-center gap-2 pt-1">
                      <Input
                        type="number"
                        min={0}
                        max={100}
                        className="h-7 text-xs font-mono w-20"
                        value={pubPct}
                        onChange={(e) => {
                          const p = Math.max(0, Math.min(100, Number(e.target.value) || 0));
                          const calcTotal = Math.round((p * 0.5) + (recPct * 0.5));
                          set({
                            publishingAiPercent: p,
                            recordingAiPercent: recPct,
                            aiPercent: calcTotal,
                            compositionHuman: p === 0,
                            level: calcTotal === 0 ? 'human' : calcTotal <= 25 ? 'ai-assisted' : 'ai-generated',
                          });
                        }}
                      />
                      <span className="text-[11px] text-muted-foreground font-mono">
                        ({100 - pubPct}% Human Written)
                      </span>
                    </div>
                  </div>

                  {/* 2. Recording Side: Master Sound Audio */}
                  <div className="space-y-1.5 p-2.5 rounded-md border bg-background/80">
                    <div className="flex items-center justify-between">
                      <Label className="text-xs font-semibold text-foreground flex items-center gap-1">
                        🎙️ 2. Recording Side
                      </Label>
                      <span className="text-xs font-mono font-bold text-amber-400">
                        {recPct}% AI
                      </span>
                    </div>
                    <p className="text-[11px] text-muted-foreground leading-tight">
                      Master sound recording, vocals &amp; instrument audio generation.
                    </p>
                    <div className="flex items-center gap-2 pt-1">
                      <Input
                        type="number"
                        min={0}
                        max={100}
                        className="h-7 text-xs font-mono w-20"
                        value={recPct}
                        onChange={(e) => {
                          const r = Math.max(0, Math.min(100, Number(e.target.value) || 0));
                          const calcTotal = Math.round((pubPct * 0.5) + (r * 0.5));
                          set({
                            publishingAiPercent: pubPct,
                            recordingAiPercent: r,
                            aiPercent: calcTotal,
                            compositionHuman: pubPct === 0,
                            level: calcTotal === 0 ? 'human' : calcTotal <= 25 ? 'ai-assisted' : 'ai-generated',
                          });
                        }}
                      />
                      <span className="text-[11px] text-muted-foreground font-mono">
                        ({100 - recPct}% Human Recorded)
                      </span>
                    </div>
                  </div>
                </div>

                <div className="text-[11px] text-muted-foreground flex items-center gap-1.5 border-t border-violet-500/20 pt-2 font-mono">
                  <span>Formula:</span>
                  <span className="text-violet-300 font-semibold">
                    ({pubPct}% Pub × 50%) + ({recPct}% Rec × 50%) = {overallPct}% Total AI
                  </span>
                </div>
              </div>
            );
          })()}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs mb-1 block">AI model / tool</Label>
              <Input className="h-8 text-xs" placeholder="e.g. Suno v4, Udio, MusicGen" value={d.model || ""} onChange={(e) => set({ model: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs mb-1 block">Model link</Label>
              <Input className="h-8 text-xs" placeholder="https://suno.com" value={d.modelUrl || ""} onChange={(e) => set({ modelUrl: e.target.value })} />
            </div>
          </div>

          {/* Composition / copyright provenance — the RECORDING can be AI while
              the underlying SONG (copyright) is 100% human-written. */}
          <div className="rounded-md border border-emerald-500/30 bg-emerald-500/5 p-2.5 space-y-2">
            <label className="flex items-start gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                className="mt-0.5 h-3.5 w-3.5 accent-emerald-500 flex-shrink-0"
                checked={!!d.compositionHuman || (d.publishingAiPercent === 0)}
                onChange={(e) => {
                  const isChecked = e.target.checked;
                  const pub = isChecked ? 0 : 100;
                  const rec = d.recordingAiPercent ?? 100;
                  const calcTotal = Math.round((pub * 0.5) + (rec * 0.5));
                  set({
                    compositionHuman: isChecked,
                    publishingAiPercent: pub,
                    recordingAiPercent: rec,
                    aiPercent: calcTotal,
                    level: isChecked ? 'ai-generated' : d.level,
                  });
                }}
              />
              <span className="text-xs">
                <span className="font-medium text-emerald-600 dark:text-emerald-400">Composition &amp; copyright are 100% human (0% AI Publishing Side)</span>
                <span className="block text-[11px] text-muted-foreground">The AI generated the audio/recording, but the music/lyrics/melody were written by human(s) — e.g. an AI rendering of an existing human composition.</span>
              </span>
            </label>
            {(d.compositionHuman || d.publishingAiPercent === 0) && (
              <div>
                <Label className="text-xs mb-1 block">Based on / recreates — original human work</Label>
                <Input className="h-8 text-xs" placeholder="e.g. Song Title — Original Artist" value={d.basedOn || ""} onChange={(e) => set({ basedOn: e.target.value })} />
              </div>
            )}
          </div>

          <div>
            <Label className="text-xs mb-1 block">Influences &amp; sources — one per line: <span className="font-mono">Name | https://source</span></Label>
            <Textarea
              rows={3}
              className="text-xs font-mono"
              placeholder={"J Dilla | https://en.wikipedia.org/wiki/J_Dilla"}
              value={(d.influences || []).map((i) => `${i.name}${i.sourceUrl ? " | " + i.sourceUrl : ""}`).join("\n")}
              onChange={(e) =>
                set({
                  influences: e.target.value.split("\n").map((l) => l.trim()).filter(Boolean).map((l) => {
                    const [name, url] = l.split("|").map((s) => s.trim());
                    return { type: "artist" as const, name, sourceUrl: url || undefined };
                  }),
                })
              }
            />
          </div>

          <div>
            <Label className="text-xs mb-1 block">Live musicians overdubbed — one per line: <span className="font-mono">Name — instrument</span></Label>
            <Textarea
              rows={2}
              className="text-xs font-mono"
              placeholder={"Jane Doe — bass"}
              value={(d.liveMusicians || []).map((m) => `${m.name}${m.instrument ? " — " + m.instrument : ""}`).join("\n")}
              onChange={(e) =>
                set({
                  liveMusicians: e.target.value.split("\n").map((l) => l.trim()).filter(Boolean).map((l) => {
                    const [name, inst] = l.split(/—|--|-/).map((s) => s.trim());
                    return { name, instrument: inst || undefined };
                  }),
                })
              }
            />
          </div>

          <div>
            <Label className="text-xs mb-1 block">🤓 Go full nerd — how you made it</Label>
            <Textarea rows={2} className="text-xs" placeholder="Walk us through it…" value={d.processNotes || ""} onChange={(e) => set({ processNotes: e.target.value })} />
          </div>
        </div>
      )}
    </div>
  );
}
